/**
 * Admin Autobuild console types — the a7y-admin page surfacing the automated
 * build sessions (CDID / AHC / APD) from the BigQuery flight recorder.
 *
 * BigQuery (`a7y_data.auto_session_log`) is the AUTHORITATIVE and only data
 * source: the session runner writes a `start` row before the session boots
 * and a `terminal` row on almost every exit path, and sessions log phase/
 * warning/error events in between. Every lifecycle state shown in the admin UI
 * is derived from those rows — no GitHub API involvement.
 *
 * "Almost" is load-bearing. The terminal row is written by an `if: always()`
 * step inside the session's own job (build-session.yml), so it cannot survive
 * the runner itself going away: on a SIGTERM from the autoscaler (exit 143)
 * GitHub skips every remaining step, `always()` included — even its own
 * `Post Run actions/checkout` cleanup. Two CDID sessions died that way on
 * 2026-08-06 (runs 31068190311 and 31098664534) and read as `running`
 * indefinitely, because the console had no cutoff and nothing backfilled the
 * row. Two defences now exist: `session-terminal-reconciler.yml` writes the
 * missing row from a separate runner after the fact, and the console treats a
 * terminal-less session past `AUTOBUILD_SESSION_TIMEOUT_MINUTES` as failed so
 * a phantom cannot outlive the reconciler failing too.
 */

export type AutobuildKind = 'cdid' | 'ahc' | 'apd';

export type AutobuildRunnerHost = 'ukko' | 'github' | 'self-hosted';

/**
 * Which ingress started a session — CDID's four, mirrored from
 * `INGRESS_LEASE_PURPOSE` in `scripts/lib/cdid-worker-lifecycle.cjs` (the one
 * registry of ingress names) and from `cdid.yml`'s `ingress` choice enum,
 * which is the structural gate on what can actually be dispatched.
 *
 * `cdid-schedule` is the 6h cron; `cdid-dispatch` is a person running
 * `/a7y-cdid-trigger` or `gh workflow run` by hand; `cdid-hot-lane` is the
 * 20-minute urgent lane; `adgs` is the deploy-gate dispatcher.
 *
 * AHC and APD have no ingress vocabulary of their own and record none, so
 * their sessions carry `null` — as does every session logged before the
 * `ingress` marker existed.
 *
 * **Null still means "not recorded" and must never be rendered as "cron"** —
 * but as of A7Y-3962 a null MARKER no longer implies a null VALUE. `ingressFor`
 * resolves two cases from other facts the row genuinely carries: a
 * `cdid-hot-lane` session IS the hot lane, and a `cdid` session whose RECORDED
 * `trigger_source` is `schedule` was started by the only schedule `cdid.yml`
 * has, which is what `cdid-schedule` names. Measured 2026-09-13, that derived
 * path is now the dominant producer — 52 rows against 47 sessions carrying any
 * recorded marker at all — because the marker covers 47 sessions and misses
 * 2,628. What the rule still forbids is inventing a value from nothing: the
 * ingress scheduler is a different workflow this enum does not describe, and it
 * derives nothing (A7Y-3962 D7).
 */
export const AUTOBUILD_INGRESSES = [
  'cdid-schedule',
  'cdid-dispatch',
  'cdid-hot-lane',
  'adgs',
] as const;

export type AutobuildIngress = (typeof AUTOBUILD_INGRESSES)[number];

/**
 * Which layer of a routine a session is. Mirrored from
 * `AUTO_SESSION_ACTIVITY_COMPONENTS` in `@a7y/platform-health` — that array is
 * the registry, this union is the console's view of it, and
 * `auto-session-activity-wiring.test.cjs` fails CI if a workflow emits a
 * component the registry does not carry.
 *
 * A7Y-3962 added `hot-lane` and `lander`. CDID is five layers, not three: the
 * hot lane admits an urgent ticket, the ingress scheduler admits the scheduled
 * ones, an implementation session orchestrates, workers implement, and the
 * lander is what actually merges the green branches onto the release line. The
 * last of those writes the `landed` ticket rows, so a console that could not
 * classify it reported every CDID run as having resolved nothing.
 */
export type AutobuildActivityComponent =
  | 'implementation-session'
  | 'worker'
  | 'ingress-scheduler'
  | 'hot-lane'
  | 'lander'
  | 'legacy-unclassified';

/**
 * Hard job timeout per kind, in minutes — the `timeout_minutes` each caller
 * passes to `build-session.yml` (default 355). cdid's 90 is the retired CDID
 * session's cap (`scripts/lib/cdid-execution-mode.cjs`); since A7Y-5421
 * cdid.yml runs only the board sweep (15) and housekeeping (30), and 90 stays
 * a ceiling above both. Past this ceiling, a session with no terminal row
 * cannot still be running: GitHub has killed the job.
 *
 * DRIFT DETECTOR: `scripts/state-invariants.test.cjs` parses the workflow YAML
 * and fails if these numbers disagree with what the callers actually declare,
 * so this table cannot go quietly stale the way its prose predecessor did (the
 * sweep asserted "none of cdid/ahc/apd override it" for ~2 weeks after
 * cdid.yml started passing 420).
 */
export const AUTOBUILD_SESSION_TIMEOUT_MINUTES: Record<AutobuildKind, number> = {
  cdid: 90,
  ahc: 355,
  apd: 355,
};

/**
 * `skipped` — the session did not do its work and said why: either it
 * declared the skip itself (for example, APD found that its frozen candidate
 * was no longer eligible) or it was superseded in the GitHub concurrency queue
 * and never started at all. Distinct from `failure`: a skip is the system
 * behaving correctly, and from `success`: nothing shipped.
 */
export type AutobuildOutcome = 'success' | 'failure' | 'running' | 'skipped';

export type AutobuildSeverity = 'info' | 'warning' | 'error';

/** One session (a single workflow run), aggregated from its log rows. */
export interface AutobuildSessionSummary {
  kind: AutobuildKind;
  component: AutobuildActivityComponent;
  /** Session stamp, e.g. "2026-07-24-00-34" (CDID/APD) or "2026-07-23-23" (AHC). */
  stamp: string;
  /** Immutable operator label captured at kickoff; absent on older sessions. */
  sessionLabel?: string | null;
  /** GitHub Actions run id when the session ran in CI; null for local runs. */
  runId: string | null;
  startedAt: string; // ISO timestamp of the first log row
  endedAt: string | null; // ISO timestamp of the terminal row; null while running
  durationSeconds: number | null; // null while running (client derives elapsed)
  outcome: AutobuildOutcome;
  /** The terminal row's message (conclusion + run URL), when ended. For a
   * skipped session this is the skip reason, suitable for inline display. */
  terminalMessage: string | null;
  warningCount: number;
  /** Error rows excluding the terminal row itself. */
  errorCount: number;
  eventCount: number;
  /**
   * Tickets this session resolved — `phase='ticket'` rows with a terminal
   * outcome (landed / adopted / superseded, or shipped for APD). Parked,
   * deferred, blocked, and evaluated tickets don't count.
   */
  resolvedTicketCount: number;
  /**
   * Tickets this session ran out of clock for — `phase='ticket'` rows with
   * outcome `deferred`. They keep their pre-batch state minus the session
   * label and are picked up by a later session, so this is a throughput
   * signal, not a failure count.
   */
  deferredTicketCount: number;
  /**
   * Tickets this session FILED rather than implemented — `phase='ticket'` rows
   * with outcome `evaluated` or `filed`.
   *
   * This is AHC's number. AHC only opens tickets, so `resolvedTicketCount` and
   * `deferredTicketCount` are structurally zero for it and reported a measured
   * zero where there was nothing to measure. The implementation outcomes are
   * excluded deliberately — see `FILED_TICKET_OUTCOMES` in
   * `a7y-api/src/backends/admin/autobuild/cycle_stages.ts` for why an AHC stamp
   * contains another session's rows at all.
   */
  filedTicketCount: number;
  /**
   * Distinct tickets the session has logged at all, whatever the outcome —
   * including the `batched` rows written when it claims its batch. A running
   * session has resolved nothing yet, so this is what its ticket count should
   * show; `resolvedTicketCount` is the meaningful number once it has ended.
   */
  ticketCount: number;
  /**
   * Per-cycle ticket-pipeline summary (A7Y-3962 D1/D4), one entry per stage
   * that currently holds at least one attributed ticket, in
   * `CDID_LIFECYCLE_STATE_ORDER`. Empty for a session this console does not
   * derive a pipeline for — a worker, a lander wave, or an AHC/APD run
   * (exclusion 6): those kinds either aren't a cycle or don't carry the
   * `CdidLifecycleState` vocabulary this is built from.
   */
  stages: AutobuildCycleStageSummary[];
  /**
   * True when a human started this run by hand via `/a7y-<kind>-trigger`
   * (GitHub `workflow_dispatch`), false when the cron fired it.
   *
   * **Null means unknown, not scheduled** — sessions logged before
   * `auto_session_log.trigger_source` existed (pre 2026-07-29) carry no
   * provenance, and an off-schedule manual run is exactly the kind of thing a
   * reader must not have silently mislabelled.
   */
  manuallyTriggered: boolean | null;

  /**
   * Where this session actually ran.
   *
   * `'ukko'` is the self-hosted fleet; `'github'` is a GitHub-hosted runner;
   * `'self-hosted'` is a self-hosted runner that is NOT UKKO — a real third
   * value rather than a fallback, because the repo has had more than one
   * self-hosted label and calling an unknown fleet "ukko" would be a guess.
   *
   * Null for sessions logged before the `runner` marker existed. As with
   * `manuallyTriggered`, null means unknown and must not render as either.
   */
  runnerHost: AutobuildRunnerHost | null;

  /**
   * The exact runner, e.g. `ukko-server-ghr-16-mtcwp66s-kgz4`. Carried for the
   * detail page: tracing a hung session to its slot needs the specific machine,
   * not the fleet — run 33164417473 was diagnosed that way on 2026-08-28.
   */
  runnerName: string | null;

  /**
   * The stamp of the session that dispatched this one, for the sessions that
   * have one — today that is a CDID worker naming its orchestrator.
   *
   * A worker's own stamp is `worker-<its own run id>-<attempt>`, so parentage
   * is not recoverable from it: before this field the Activity tab listed ~10
   * workers per cycle as flat peers of the implementation session, with nothing
   * saying which cycle any of them came from. The value is whatever the
   * dispatcher passed as `parent_session_id` (`cdid-dispatch-wave.cjs` passes
   * the orchestrator's stamp), recorded by build-session.yml as a
   * `parent-session` phase row.
   *
   * **Null means no parent OR not recorded, and the console must not
   * distinguish them by guessing** — an orchestrator, AHC and APD are genuine
   * roots, and every session logged before the marker existed is unrecorded.
   * Both render at the top level, which is correct for a root and honest for
   * the rest. A non-null value is not a promise that the parent is in the same
   * response: history is bounded server-side per (kind, stamp prefix,
   * has-tickets) with each root's children riding along (A7Y-3962 D3), so a
   * worker whose root fell outside the bound can still outlive its
   * orchestrator's window and must still render.
   */
  parentStamp: string | null;

  /**
   * Which ingress started this session, when it recorded one.
   *
   * This is what `manuallyTriggered` could not answer. Every machine ingress
   * dispatches `cdid.yml` with `gh workflow run` under a GitHub App
   * installation, so GitHub records `event=workflow_dispatch` with an actor
   * that is neither `github-actions[bot]` nor a person — indistinguishable
   * from a hand-typed trigger. Measured 2026-09-05: 48 of the 92 CDID
   * orchestrator sessions over 14 days were badged "Manual" on that basis.
   *
   * Null means the console could resolve no ingress at all — AHC and APD never
   * record one, and neither did any session predating the marker. A null
   * MARKER is not the same thing: `ingressFor` resolves `cdid-hot-lane` from
   * the session kind and `cdid-schedule` from a recorded `trigger_source` of
   * `schedule` on a `cdid` session (A7Y-3962 K2), because the marker covers 47
   * sessions and misses 2,628 while the Close-out requires every run badged by
   * trigger. Absence is still never turned into cron — the derivation reads a
   * recorded column on the workflow this enum describes.
   */
  ingress: AutobuildIngress | null;
}

export interface AutobuildRoutineOverview {
  kind: AutobuildKind;
  /** Human label, e.g. "CDID — Continuous Development-Integration-Deploy". */
  label: string;
  /** Human schedule description, e.g. "Every 4 hours at :00 UTC". */
  scheduleLabel: string;
  /** Next cron slot (UTC, ISO). GitHub typically fires 15–60 min after it. */
  nextScheduledAt: string;
  latest: AutobuildSessionSummary | null;
}

export interface AutobuildOverview {
  routines: AutobuildRoutineOverview[];
  generatedAt: string;
}

/**
 * Every account the quota surfaces can name — GENERATED from
 * `ACCOUNT_POLICY_ORDER`, and re-exported here so no consumer's import changes.
 *
 * A7Y-3797 ended the hand maintenance. The union still cannot be inferred by
 * TypeScript (`a7y-api` sets `allowJs: false` and the registry is a
 * zero-dependency `.cjs` that ships no `.d.ts`, so the module reads as `any`),
 * but it is no longer written by hand either: `npm run generate:claude-accounts`
 * emits `./generated/ai-quota-account-key.js` from the policy constant, and
 * `scripts/claude-account-registry.test.cjs` fails CI when the committed file
 * and the constant disagree.
 *
 * The import/re-export PAIR is deliberate. A bare
 * `export type { AiQuotaAccountKey } from './generated/…'` re-exports the name
 * without binding it in this module's scope, which is TS2304 at the five uses
 * further down this file.
 */
import type { AiQuotaAccountKey } from './generated/ai-quota-account-key.js';

export type { AiQuotaAccountKey };

export type AiQuotaProvider = 'claude' | 'codex';

/** One provider-defined quota window, classified by its duration rather than
 * its `primary` / `secondary` slot (those slots are not semantically stable). */
export interface AiQuotaWindow {
  usedPercent: number;
  resetsAt: string | null;
  windowDurationMins: number | null;
}

/**
 * One per-model weekly limit on an account (A7Y-3814).
 *
 * WHAT `usedPercent: 100` MEANS, stated because it is easy to overstate (TPF,
 * 2026-09-09): that MODEL cannot be used on this account until the weekly
 * window cycles. It says nothing about the account, which keeps running work
 * routed to every other model — so this is never a reason to call an account
 * exhausted, and `AiQuotaAccountStatus.weekly` remains the account-wide figure.
 *
 * Measured 2026-09-09 on `tpf`: `weekly.usedPercent` was 86 while Fable's
 * scoped limit read 100 at `severity: critical`. Both true, about different
 * windows — and only the first of them used to leave the probe.
 */
export interface AiQuotaScopedLimit {
  /** The provider's own display name, e.g. `Fable`. Never an internal model id. */
  model: string;
  usedPercent: number;
  /** The provider's word — `normal` | `warning` | `critical`. Null when it sent
   *  none; the percentage is the fact, this is only its emphasis. */
  severity: string | null;
  /** The endpoint's `is_active`, carried verbatim. It marks the window the
   *  provider considers current — NOT "this account is out of quota". */
  isActive: boolean;
  resetsAt: string | null;
}

export interface AiQuotaRefreshSource {
  /** CDID/AHC/APD for session refreshes; `manual` for the dashboard button. */
  kind: AutobuildKind | 'manual';
  stamp: string | null;
  runId: string | null;
}

/**
 * The admission boundaries one account's readings are judged against, supplied
 * by the API from the single admission policy (A7Y-2821 §2) so that no client
 * restates them.
 *
 * PER-ACCOUNT, NOT PER-STATUS, because the ceiling genuinely differs by
 * account: `windowsFor()` runs the Claude accounts to `POLICY.admissionCeilingPct`
 * and codex to its own weekly pool at `POLICY.codexWeeklyCeilingPct`. A single
 * policy-wide ceiling on `AiQuotaStatus` would paint codex as over-budget from
 * the Claude number, which is a boundary that lies — the exact defect the
 * hardcoded `>= 40` colour scale had. `quarantinePct` is policy-wide and rides
 * along here so a rendered row carries both of its boundaries in one object.
 */
export interface AiQuotaThresholds {
  /** Below this, new automation work is admitted on this account (exclusive). */
  admissionCeilingPct: number;
  /** At or above this the account leaves the pool entirely until it resets. */
  quarantinePct: number;
}

/**
 * The role an account plays in the fleet, from `accountRole()` in
 * `scripts/lib/quota-reservation.cjs` — the same answer the CDID lane planner
 * uses, never a second classification.
 *
 *   `orchestrator`   `team@`, pinned 2026-08-26. Hosts the session that selects,
 *                    plans, reviews, verifies, lands and reports. Never a worker
 *                    build — it is removed from the worker driver pool and from
 *                    the worker implementation view.
 *   `review-only`    `codex` (A7Y-3054). Still probed, still ranked, still the
 *                    preferred reviewer when Claude implemented; may not build.
 *   `implementation` may host worker implementation.
 */
export type AiQuotaAccountRole = 'orchestrator' | 'implementation' | 'review-only';

/** Cached quota state for one coding account. A failed refresh leaves the last
 * successful values in place and records the newer error separately. */
export interface AiQuotaAccountStatus {
  account: AiQuotaAccountKey;
  /** Short display form, e.g. `ops+claude01@`. */
  label: string;
  /**
   * The mailbox this account signs in as, e.g. `ops+claude01@apptonomy.ai`.
   *
   * The console shows the FULL address rather than the short label: the label is
   * a display form, and `tpf@` appears twice under two providers, so a truncated
   * form is not enough to tell an operator which credential a reading belongs
   * to. Sourced from the one account table
   * (`scripts/lib/claude-accounts.cjs`), never restated.
   */
  email: string;
  provider: AiQuotaProvider;
  /** What this account may be used for. Optional for the same forward-
   * compatibility reason as `thresholds` below — the admin app and the Worker
   * deploy independently, so a client may be newer than its API. A client that
   * receives no role renders no role badge rather than guessing one. */
  role?: AiQuotaAccountRole;
  session: AiQuotaWindow | null;
  weekly: AiQuotaWindow | null;
  /**
   * Per-model weekly limits, when the provider reported any.
   *
   * `null` means NOT READ — a failed probe, a codex account (whose payload has
   * no per-model breakdown), or a row written before this field existed. `[]`
   * means read, and there are none. The two must not render alike: one is
   * ignorance, the other is an answer.
   */
  scopedLimits?: AiQuotaScopedLimit[] | null;
  /** Admission boundaries for this account. Optional for the same reason
   * `AiQuotaStatus.manualRefresh` is: the admin app and the Worker deploy
   * independently, so a client can be newer than the API it is talking to. A
   * client that does not receive them renders a neutral bar — never a guessed
   * boundary, which would be a second admission regime wearing a fallback. */
  thresholds?: AiQuotaThresholds;
  /** Timestamp of the most recent refresh attempt, successful or not. */
  updatedAt: string | null;
  /** Timestamp/source that produced the displayed quota values. */
  lastSuccessfulAt: string | null;
  source: AiQuotaRefreshSource | null;
  /** Error from the latest attempt; null when that attempt succeeded. */
  error: string | null;
}

export interface AiQuotaStatus {
  accounts: AiQuotaAccountStatus[];
  /** Claude account selected by the most recent automation quota waterfall.
   * Codex is tracked separately and is never part of that waterfall. */
  eligibleAccount: AiQuotaAccountKey | null;
  generatedAt: string;
  /** Manual refresh is disabled when this Worker cannot dispatch the GitHub
   * workflow. Optional so older API deployments remain readable. */
  manualRefresh?: {
    available: boolean;
    unavailableReason: string | null;
  };
}

export interface AiQuotaRefreshResponse {
  accepted: true;
  requestedAt: string;
}

export interface AutobuildHistory {
  kind: AutobuildKind;
  sessions: AutobuildSessionSummary[];
}

export interface AutobuildLogEvent {
  ts: string; // ISO
  phase: string;
  severity: AutobuildSeverity;
  message: string;
}

/**
 * A Linear ticket a session touched, from its latest `phase='ticket'` lifecycle
 * row (`A7Y-<n>|<outcome>|<title>`). The first row records the queued ticket and
 * title; later rows advance it through active/review/terminal outcomes and may
 * omit the already-recorded title. Sessions predating structured ticket logging
 * fall back to regex-extracted id mentions with outcome "mentioned" and no title.
 * Linear URLs are constructed client-side from the id.
 */
export interface AutobuildSessionTicket {
  /** Linear identifier, e.g. "A7Y-2029". */
  id: string;
  /** Linear title from the latest non-empty lifecycle row; null for legacy "mentioned" rows. */
  title: string | null;
  /** batched | in-progress | in-review | landed | parked | adopted | deferred | blocked | superseded | evaluated | shipped (APD) | mentioned (legacy). */
  outcome: string;
  /** Linear priority label (Urgent|High|Medium|Low) at render time, or null when
   *  unset or when the Linear lookup failed. Read live rather than logged by the
   *  session so historical runs show priorities too; the trade-off is that it
   *  reflects the ticket's CURRENT priority, not its priority when batched. */
  priority: string | null;
  /**
   * CDID lifecycle stage this ticket currently occupies (A7Y-3962 D4),
   * derived in TypeScript from its attributed facts — never a second SQL
   * mapping of the same outcome vocabulary (D4/DRY). Null when the ticket's
   * latest mapped outcome carries no stage (`dispatched`, a legacy
   * "mentioned" row, or a session this console derives no pipeline for —
   * exclusion 6).
   */
  stage: CdidLifecycleState | null;
  /**
   * ISO instant this ticket ENTERED `stage` — a later fact that maps to the
   * SAME stage does not reset it (D4). Null alongside `stage`.
   */
  stageEnteredAt: string | null;
  /**
   * ISO instant of this ticket's first `queued`-mapped fact (admission),
   * independent of whatever stage it currently occupies. Null when the
   * ticket was never recorded as admitted.
   */
  admittedAt: string | null;
  /** Latest non-null bundle id a fact named for this ticket. Null before
   *  bundling, or for a session kind whose facts never name one. */
  bundleId: string | null;
  /** Latest non-null attempt number a fact named for this ticket's bundle. */
  attempt: number | null;
  /** Latest non-null implementation account (the `driver=` on a worker's own
   *  `worker green:` row) a fact named for this ticket. */
  implementationAccount: string | null;
}

/** Quota reading captured at one boundary of an automated coding session. */
export interface AutobuildSessionQuotaSnapshot {
  observedAt: string;
  session: AiQuotaWindow | null;
  weekly: AiQuotaWindow | null;
  error: string | null;
}

/**
 * Why the waterfall did or didn't land on an account.
 *
 * - `selected`     — the session's IMPLEMENTATION ran on this account.
 * - `driver`       — this account drove the session (orchestration, Architect,
 *                    Karen, verify) while implementation ran elsewhere. Judged
 *                    against the 85% driver boundary, not a rung ceiling —
 *                    conflating the two produced a false "selected at 54%,
 *                    ABOVE its 40% ceiling" alarm on 2026-08-03-10-57.
 * - `review-only`  — A ROLE, NOT A PASS-OVER. The account is ranked in the
 *                    admission policy's own order and keeps its own ceiling and
 *                    its own reading, but `IMPLEMENTATION_ORDER` excludes it, so
 *                    it occupies no implementation rung. Codex since A7Y-3054.
 *                    Ranked below `unavailable` in `decideAccount` because a
 *                    failed probe on the cross-provider reviewer tells an
 *                    operator more than a role label does, and above
 *                    `not-reached` because "it was never in the walk" is truer
 *                    than "the walk stopped at an earlier rung".
 * - `over-ceiling` — probed fine, but its utilization was at or above the
 *                    ceiling this rung must clear.
 * - `not-reached`  — the waterfall stopped at an earlier rung, so this one was
 *                    never evaluated. Its reading is still shown: it is the
 *                    evidence the reserve was intact.
 * - `unavailable`  — no credentials, or the quota probe failed.
 */
export type AutobuildQuotaDecision =
  'selected' | 'driver' | 'review-only' | 'over-ceiling' | 'not-reached' | 'unavailable';

/** One coding account the session probed, its quota at the moment the session
 * started, and the waterfall's verdict on it. Every configured account appears
 * — not just the one that ran — because the skipped rungs are the evidence the
 * selection was correct. */
export interface AutobuildSessionQuotaAccount {
  account: AiQuotaAccountKey;
  label: string;
  provider: AiQuotaProvider;
  before: AutobuildSessionQuotaSnapshot | null;
  after: AutobuildSessionQuotaSnapshot | null;
  /** 1-based position in the implementation waterfall; null for the deep reserve. */
  rung: number | null;
  /** Weekly ceiling this rung must be under to be picked; null when it has none. */
  ceilingPct: number | null;
  decision: AutobuildQuotaDecision;
  /** One short clause carrying the decision's reason, e.g. "44% ≥ 40% ceiling". */
  note: string;
}

/* ─────────────────────── A7Y-2821 automation control plane ─────────────────
 *
 * The lease/reservation vocabulary the admin console reads out of
 * `AutomationDriverLeases` (Firestore) and `POLICY`
 * (`scripts/lib/quota-reservation.cjs`).
 *
 * THE CAPACITY CONCEPTS ARE SEPARATE FIELDS, NOT ONE NUMBER. The epic's §7
 * requirement — never describe hitting our own ceiling as running out of
 * provider quota — is enforced here by shape rather than by naming discipline:
 * `providerHardLimitPct` and `operatingCeilingPct` are separate, separately
 * labelled, and a refusal names WHICH of them bound it via
 * `LimitingResource.capacityKind`. A7Y-3699 retired the third field this used
 * to name, `reservedPct` — there is no reservation envelope left to hold
 * capacity, only the live `observedPct` a fresh probe measured. A single
 * "limit" field would have made the conflation expressible again.
 */

/**
 * Which capacity a refusal ran out of. The wire vocabulary is deliberately more
 * explicit than the internal comparator's (`hard_limit` / `internal_ceiling` /
 * `reserved_capacity` in `cdid-resource-scheduler.cjs`): a reader of
 * `hard_limit` has to already know whose limit it is, and that ambiguity is the
 * one this ticket exists to remove. The API maps at the boundary.
 *
 * - `provider_hard_limit` — the PROVIDER refused, or would: 100% of a window.
 *   This is the only value that means "we are out of quota".
 * - `operating_ceiling`   — OUR OWN ceiling refused it. The provider still has
 *   headroom. Rendering this as exhaustion is the specific lie A7Y-2821 forbids.
 * - `reserved_capacity`   — the window's own reservations, held by live work,
 *   leave no room. Nothing is exhausted and no ceiling was crossed; capacity is
 *   simply spoken for and will return when that work finishes.
 */
export type LimitingResourceKind =
  'provider_hard_limit' | 'operating_ceiling' | 'reserved_capacity';

/** The comparator's verdict on one refused request, as rendered by the console. */
export interface LimitingResource {
  /** What was being asked for, e.g. `claude02:weekly`. */
  resource: string;
  capacityKind: LimitingResourceKind;
  /** The provider's own bound on the window (100), or null when unbounded. */
  hardLimit: number | null;
  /** Our admission ceiling for this window, or null when there is none. */
  operatingCeiling: number | null;
  /** Points already observed when the comparison was made (A7Y-3699: no
   *  reservation is added — the comparator's `used`, not observed+reserved). */
  reserved: number;
  /** The observed reading itself, compared against the ceiling (A7Y-3699: no
   *  separate "request" any more — the reading IS the requested figure). */
  requested: number;
  /** The comparator's own sentence. Rendered verbatim — never re-worded into
   *  exhaustion language, which is how an operating-ceiling stop becomes a
   *  false "we're out of quota". */
  message: string;
}

export type AutomationLeaseState =
  'free' | 'reserved' | 'active' | 'handoff_pending' | 'quarantined';

/**
 * A7Y-3699 — `AutomationReservation` is RETIRED. `admit()` no longer sizes a
 * per-holder envelope (a "reservation"); a holder now carries only a
 * descriptive work-phase LABEL (`AutomationLease.phase`), which buys no
 * capacity of its own. There is nothing left for this type to describe.
 */

/** One window of one account, with the two remaining capacity concepts kept apart. */
export interface AutomationCapacityWindow {
  window: 'five_hour' | 'weekly';
  /** OUR ceiling, exclusive. From `POLICY` via `windowsFor()`. */
  operatingCeilingPct: number;
  /** The reading the control plane last recorded for this window; null when it
   *  recorded none. Null is NOT zero — an unread window is unknown. */
  observedPct: number | null;
}

export interface AutomationCapacity {
  /** 100. The provider's bound on a percentage window — structural, not policy. */
  providerHardLimitPct: number;
  windows: AutomationCapacityWindow[];
  /** Populated only when the recorded reading actually refuses admission. */
  limitingResource: LimitingResource | null;
  /**
   * Why no historical capacity verdict was computed — for example a missing,
   * unreadable, or future-dated reading. Mutually exclusive with
   * `limitingResource`, and never rendered as a stop: "we could not evaluate"
   * and "the snapshot did not fit" are different facts.
   */
  notEvaluatedReason: string | null;
}

/** Who holds a lease, and where to go and look at it. */
export interface AutomationLeaseHolder {
  workflow: string | null;
  runId: string | null;
  runAttempt: string | null;
  job: string | null;
  pid: number | null;
  /** GitHub run URL, when the run id is known. */
  runUrl: string | null;
}

/**
 * One account's lease document, as rendered.
 *
 * ABSENCE IS THREE DIFFERENT FACTS AND THEY ARE KEPT APART: `readError` set
 * means we could not look; `documentExists: false` means we looked and the
 * account has never been leased; `state: 'free'` means we looked and nobody
 * holds it. Collapsing the first into either of the others would let a
 * Firestore outage render as a healthy idle fleet.
 */
export interface AutomationLease {
  account: AiQuotaAccountKey;
  documentExists: boolean;
  /**
   * How many slots this account currently holds (A7Y-3285).
   *
   * The lease is a counted semaphore, not a mutex, so "is anybody on this
   * account" is no longer the operator's question — "how full is it" is. Every
   * field below that describes A holder describes the FIRST one; the count is
   * what says whether there are others.
   */
  holderCount: number;
  /** The FIRST holder's state. Null when the account holds nobody. */
  state: AutomationLeaseState | null;
  /** The fencing token, ACCOUNT-level. Monotonic; every acquisition increments it. */
  generation: number | null;
  /** The FIRST holder. `holderCount` says how many others there are. */
  holder: AutomationLeaseHolder | null;
  purpose: string | null;
  queueId: string | null;
  bundleId: string | null;
  attemptId: string | null;
  ticketIds: string[];
  acquiredAt: string | null;
  renewedAt: string | null;
  /** When an unrenewed lease stops being honoured. Drives the countdown. */
  expiresAt: string | null;
  /** The quota reading the holder was admitted on. */
  sample: {
    takenAt: string | null;
    fiveHourPct: number | null;
    weeklyPct: number | null;
    resetsAt: string | null;
    probeOk: boolean;
  } | null;
  /** The FIRST holder's work-phase label (A7Y-3699) — descriptive only. */
  phase: string | null;
  /** Account-level: a quarantine bans the account, not one slot. */
  quarantine: {
    until: string | null;
    reason: string | null;
    evidence: string | null;
  } | null;
  lastIncident: { code: string | null; at: string | null } | null;
  capacity: AutomationCapacity;
  /** Why this one account could not be read. Null on a successful read. */
  readError: string | null;
}

/**
 * A lease parked in `handoff_pending`: the work is checkpointed, the account is
 * still held, and nothing is running on it.
 *
 * No "stalled" flag. The ten-minute bound belongs to the watchdog
 * (`cdid-control-watchdog.cjs`), which pages on it; restating the number here
 * would be a second copy of a policy bound in a surface that cannot enforce it.
 * The console reports the age and lets the watchdog own the verdict.
 */
export interface AutomationHandoff {
  account: AiQuotaAccountKey;
  generation: number | null;
  /** When the lease was last written, i.e. when the handoff began. */
  since: string | null;
  ageMs: number | null;
  bundleId: string | null;
  attemptId: string | null;
  ticketIds: string[];
  /** The last recorded incident code, when one explains the handoff. */
  reason: string | null;
}

/**
 * Scheduler-circuit state for a queue partition.
 *
 * `unknown` IS A FIRST-CLASS VALUE AND IS THE CURRENT ONE.
 * `scheduler-circuit.cjs` is a pure module that persists nothing, and no
 * dispatcher calls its `evaluate()` yet, so nothing writes a circuit state
 * anywhere this endpoint can read. Rendering that as `closed` would report a
 * healthy circuit purely because nothing observed it — the failure this ticket's
 * whole observability half exists to stop. `reason` always says which it is.
 */
export interface AutomationCircuitState {
  state: 'open' | 'closed' | 'unknown';
  partition: string | null;
  openedAt: string | null;
  /** Identical non-progress passes counted when it opened. */
  passes: number | null;
  /** `POLICY.fingerprintPassBound` — the run length that opens a circuit. */
  bound: number | null;
  fingerprint: string | null;
  /** Always populated for `unknown`; names what did not report. */
  reason: string | null;
}

/** `GET /admin/autobuild/leases`. */
export interface AutomationLeaseStatus {
  leases: AutomationLease[];
  handoffs: AutomationHandoff[];
  circuit: AutomationCircuitState;
  generatedAt: string;
}

export type CdidLifecycleState =
  | 'queued'
  | 'planning'
  | 'implementing'
  | 'in_review'
  | 'fix_in_flight'
  | 'green'
  | 'landing'
  | 'landed'
  | 'blocked'
  | 'parked'
  | 'deferred';

/**
 * Pipeline order for `CdidLifecycleState` (A7Y-3962) — the order a ticket
 * actually moves through the stages the admin console renders, and the order
 * `AutobuildCycleStageSummary[]` is walked in. An explicit array rather than a
 * derived key order: nothing about the union's declaration order is a
 * contract, and this is one.
 */
export const CDID_LIFECYCLE_STATE_ORDER: readonly CdidLifecycleState[] = [
  'queued',
  'planning',
  'implementing',
  'in_review',
  'fix_in_flight',
  'green',
  'landing',
  'landed',
  'blocked',
  'parked',
  'deferred',
];

/**
 * States a ticket does not move on from within a session (A7Y-3962 D5) — the
 * point past which the session-detail page's poll may stop, because nothing
 * still in flight can change these tickets' stage. `green` is deliberately
 * NOT terminal: a green ticket is waiting to land, which is exactly the
 * "settles hours after the session ends" case D5 exists to show.
 */
export const CDID_TERMINAL_LIFECYCLE_STATES: ReadonlySet<CdidLifecycleState> = new Set([
  'landed',
  'blocked',
  'parked',
  'deferred',
]);

/**
 * One stage bucket in a cycle's ticket pipeline (A7Y-3962 D1/D4/D5).
 * `enteredAt` is the EARLIEST `stageEnteredAt` among the tickets currently
 * bucketed here — the oldest ticket waiting in this stage, which is the one
 * that should drive a "how long has this cycle been stuck" reading.
 */
export interface AutobuildCycleStageSummary {
  stage: CdidLifecycleState;
  ticketCount: number;
  enteredAt: string;
}

export type CdidThroughputOutcome = 'running' | 'completed' | 'completed_degraded';
export type CdidSloStatus = 'pass' | 'fail' | 'unknown' | 'not_applicable';
export type AddisSource = 'cdid' | 'ahc' | 'adgs';

export interface AddisSourceRecord {
  version: 'addis.source/v1';
  source: AddisSource;
  eventId: string;
  fingerprint: string;
  ticketIds: string[];
  readinessAuthority: 'cdid-evaluation' | 'ahc-evidence' | 'adgs-deploy-gate';
  evidenceLinks: string[];
  enqueuedAt: string;
  correlationId: string;
  revalidationTrigger: string | null;
  evidence?: unknown;
}

export interface CdidBundleTimings {
  bundleId: string;
  queueWaitMs: number;
  planningMs: number;
  implementationMs: number;
  reviewMs: number;
  fixMs: number;
  greenWaitMs: number;
  landMs: number;
  totalMs: number;
}

export interface CdidLifecycleBundle {
  bundleId: string;
  workGroupId: string;
  ticketIds: string[];
  provider: 'claude' | 'codex';
  driverAccount: string;
  implementationAccount: string;
  teamTier: 'solo' | 'standard' | 'full';
  footprint: string[];
  dependencies: string[];
  state: CdidLifecycleState;
  attempt: number;
  timings: CdidBundleTimings;
}

export interface AddisLifecycleBundle extends CdidLifecycleBundle {
  sourceCount: number;
  sources: AddisSourceRecord[];
}

export interface CdidLaneWindow {
  laneId: string;
  provider: 'claude' | 'codex';
  account: string;
  availableMs: number;
  usefulMs: number;
  utilizationPct: number | null;
}

export interface CdidIdleRunnableInterval {
  startedAt: string;
  endedAt: string;
  durationMs: number;
  bundleIds: string[];
  compatibleLaneIds: string[];
}

export interface CdidStructuredStop {
  reason: string;
  remainingMs: number;
  requiredReserveMs: number;
  runnableBundleIds?: string[];
  compatibleLaneIds?: string[];
  sufficientReserve?: boolean;
  capacity?: {
    kind: 'hard_limit' | 'internal_ceiling' | 'reserved_capacity';
    provider?: string;
    account?: string;
    usedPercent?: number;
    ceilingPercent?: number;
  };
}

export interface CdidThroughputSlo {
  key: string;
  target: string;
  status: CdidSloStatus;
  value: number | string | null;
  detail: string;
}

export interface CdidSessionThroughput {
  schema: 'cdid.lifecycle/v1';
  sessionStamp: string;
  generatedAt: string;
  outcome: CdidThroughputOutcome;
  bundles: CdidLifecycleBundle[];
  verifyMs: number;
  laneWindows: CdidLaneWindow[];
  laneUtilizationPct: number | null;
  idleRunnableIntervals: CdidIdleRunnableInterval[];
  maxIdleRunnableMs: number;
  stop: CdidStructuredStop | null;
  landedTicketCount: number;
  totalCostUsd: number | null;
  totalUsageUnits: number | null;
  costPerLandedTicketUsd: number | null;
  slos: CdidThroughputSlo[];
}

export interface AddisSourceThroughput {
  source: AddisSource;
  bundleCount: number;
  ticketCount: number;
  landedTicketCount: number;
  queueWaitMs: number;
  planningMs: number;
  implementationMs: number;
  reviewMs: number;
  fixMs: number;
  greenWaitMs: number;
  verifyMs: number;
  landMs: number;
  totalMs: number;
  costUsd: number | null;
  outcomes: Partial<Record<CdidLifecycleState, number>>;
  limitingResources: string[];
  stopReasons: string[];
}

export interface AddisSessionThroughput extends Omit<CdidSessionThroughput, 'schema' | 'bundles'> {
  schema: 'addis.lifecycle/v1';
  queueId: string;
  sessionCount: number;
  sessions: Array<{ stamp: string; openedAt: string; deadlineAt: string }>;
  bundles: AddisLifecycleBundle[];
  sourceThroughput: AddisSourceThroughput[];
}

export type AutobuildSessionThroughput = CdidSessionThroughput | AddisSessionThroughput;

export interface AutobuildSessionDetail {
  summary: AutobuildSessionSummary;
  /** Tickets the session touched (see AutobuildSessionTicket). */
  tickets: AutobuildSessionTicket[];
  /** Coding account(s) selected by the session, with before/after quota. */
  quotaAccounts: AutobuildSessionQuotaAccount[];
  /** ADDIS/CDID persisted lifecycle projection. Null for legacy sessions and non-coding routines. */
  throughput: AutobuildSessionThroughput | null;
  /** Chronological (ascending) log rows for the session. */
  events: AutobuildLogEvent[];
}

/**
 * One ticket in the live CDID selection queue (admin Tickets tab). The queue
 * mirrors the CDID session's Phase-1 candidate filter (a7y-cdid-session.md):
 * TPF-assigned Triage/Backlog/Todo tickets run through the project/initiative
 * gate, start-date gate, work-container and blocked-by exclusions. Work-container status is an explicit `work-container`
 * label, never inferred from having children (A7Y-2944).
 */
export interface CdidQueueTicket {
  /** Linear identifier, e.g. "A7Y-2048". */
  identifier: string;
  title: string;
  /** Linear app URL for the ticket. */
  url: string;
  /** Workflow state name: Triage | Backlog | Todo. */
  stateName: string;
  projectName: string | null;
  /** Urgent | High | Medium | Low; null when unset. */
  priorityLabel: string | null;
  /**
   * Parent ticket identifier, e.g. "A7Y-2299" — null for a top-level ticket.
   * The admin queue groups by this, so a reader can see at a glance which epic
   * a batch of eligible work belongs to rather than reading ids one by one.
   */
  parentIdentifier: string | null;
  /** Parent ticket title, for the group heading. Null when there is no parent. */
  parentTitle: string | null;
  /** null for eligible tickets; the session-style exclusion reason otherwise. */
  reason: string | null;
  /** Claim state derived from both the label and claim-owning run evidence. */
  claim: CdidClaimState;
  /**
   * In-epic blockers this ticket will be sequenced after **within the same
   * session**. Non-empty only on eligible tickets: an epic's internal `blocks`
   * relations are implementation sequencing, not deploy gates, so a child is
   * admitted once each blocker is merged or is itself admitted this round.
   */
  dependsOn: string[];
}

/**
 * How a `cdid`-labelled ticket's claim reads against run evidence.
 * Never inferred from the label alone (A7Y-2602).
 */
export type CdidClaimState = 'none' | 'active' | 'recovery_pending' | 'unverified';

/** A claim-owning run the queue proved live from the flight recorder. */
export interface CdidClaimSession {
  kind: 'cdid' | 'cdid-hot-lane';
  stamp: string;
  runId: string | null;
  startedAt: string;
}

export interface CdidQueue {
  /** Would be admitted to eval by a session running now, in selection order. */
  eligible: CdidQueueTicket[];
  /** Claims backed by a live run, or unverified while run evidence is unavailable. */
  inFlight: CdidQueueTicket[];
  /**
   * Labelled with no live claim-owning run: residue from a crashed session.
   * The next Part A sweep releases these; they are NOT being built.
   */
  recoveryPending: CdidQueueTicket[];
  /**
   * Runs proven live from a7y_data.auto_session_log. Empty plus available
   * evidence means nothing is running, whatever the labels say.
   */
  claimSessions: CdidClaimSession[];
  /**
   * False when the BigQuery probe failed. The UI must then report uncertainty
   * rather than asserting either active or stale claim state.
   */
  claimEvidenceAvailable: boolean;
  /**
   * @deprecated Always empty since A7Y-4028. Titles have no scheduling authority.
   * Kept for response compatibility; deploy-labelled work belongs to ADGS.
   */
  triggerGated: CdidQueueTicket[];
  /** Reviewed and excluded by a hard gate, with reasons. */
  excluded: CdidQueueTicket[];
  generatedAt: string;
}

/**
 * A ticket the next APD run will ship. The deploy takes whatever is on the
 * active `release/*` branch and advances the shipped tickets to On Prod, so
 * the queue is everything merged but not yet in production.
 */
export interface ApdDeployTicket {
  /** Linear identifier, e.g. "A7Y-2110". */
  identifier: string;
  title: string;
  url: string;
  /** `Done in Dev` or `On Staging` — the two pre-production states. */
  stateName: string;
  projectName: string | null;
  updatedAt: string;
  /**
   * Parent ticket identifier, e.g. "A7Y-2299" — null for a top-level ticket.
   * The admin queue groups by this, so a reader can see at a glance which epic
   * a batch of eligible work belongs to rather than reading ids one by one.
   */
  parentIdentifier: string | null;
  /** Parent ticket title, for the group heading. Null when there is no parent. */
  parentTitle: string | null;
}

export interface ApdDeployQueue {
  /** On Staging first, then newest ticket first. */
  tickets: ApdDeployTicket[];
  generatedAt: string;
}

/** One deploy-trigger ticket evaluated by the live ADGS predicate. */
export interface AdgsQueueTicket {
  identifier: string;
  title: string;
  url: string;
  stateName: string;
  projectName: string | null;
  /** Exact string returned by the production ADGS decision; null when ready. */
  reason: string | null;
  /** ISO instant the last gate target entered a satisfying state, when known. */
  gateTrippedAt: string | null;
  /** Elapsed whole seconds since gateTrippedAt, clamped at zero. */
  secondsSinceGateTripped: number | null;
  /** The same >6h condition measured by state-invariants assertion `deploy-trigger-pickup`. */
  pickupBudgetBreached: boolean;
}

/** One recent execution of the stateless adgs.yml sweep. */
export interface AdgsSweep {
  runId: string;
  url: string;
  /** Deploy Staging | Deploy Production | schedule | manual (or GitHub fallback). */
  trigger: string;
  status: string;
  conclusion: string | null;
  startedAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
  /** Null means the run log is not readable yet; it never means zero. */
  evaluatedCount: number | null;
  /** Null means dispatch detail could not be proven from a CDID run title. */
  dispatchedTicketIds: string[] | null;
  /**
   * Which fleet ran the sweep. Null means GitHub recorded no runner for it —
   * never "GitHub-hosted", which is its own value. `adgs.yml` resolves its
   * `runs-on` from `vars.A7Y_RUNNER_MISC` with an `ubuntu-latest` fallback, so
   * both fleets occur in real history and a reader cannot infer one.
   */
  runnerHost: AutobuildRunnerHost | null;
  /** The machine, e.g. `ukko-server-ghr-2-mu3s2xqo-i0cr`. Null when unrecorded. */
  runnerName: string | null;
  detailsAvailable: boolean;
  detailsError: string | null;
}

/**
 * Every sweep inside one caller-chosen instant window — the day-scoped table.
 *
 * The bounds are echoed back so a response can be matched to the request that
 * asked for it; the CLIENT computes them from its own local calendar day,
 * because the server has no way to know the viewer's zone.
 */
export interface AdgsSweepWindow {
  /** Inclusive ISO start, exactly as requested. */
  from: string;
  /** Exclusive ISO end, exactly as requested. */
  to: string;
  /** Newest first, as GitHub returns them. */
  sweeps: AdgsSweep[];
  /** The page cap was reached, so older runs inside the window are missing. */
  truncated: boolean;
  generatedAt: string;
}

export interface AdgsQueue {
  /** Tripped tickets the next production sweep decision would dispatch. */
  wouldDispatchNow: AdgsQueueTicket[];
  /**
   * Tripped tickets beyond an explicitly configured `ADGS_MAX_TICKETS` cap.
   * Empty on a default sweep: ADGS has been uncapped since 2026-08-23 (TPF).
   */
  beyondCap: AdgsQueueTicket[];
  /** Deploy gates still waiting, with the exact laggard reason. */
  gateNotSatisfied: AdgsQueueTicket[];
  /**
   * Filings ADGS holds because their shape is wrong, including deploy-gate
   * metadata conflicts — an unknown `deploy-gate:*` child, or two of them at
   * once. Both arrive through the one shared malformed-shape reader, which
   * reports the conflict ahead of any shape defect.
   *
   * A conflict is actionable drift, not history, so this bucket is rendered
   * expanded: a held ticket that nothing dispatches and nothing reports is the
   * exact hole the shared gate module exists to close. Terminal tickets never
   * appear here because `ADGS_CANDIDATE_FILTER` no longer fetches them
   * (TPF, 2026-09-07).
   */
  malformed: AdgsQueueTicket[];
  /**
   * Deliberate, self-resolving holds: a `cdid` / `ahc` / `wip:interactive` claim
   * label, or a live `adgs:time-gated` verdict. Nothing is wrong with these —
   * someone or something already holds the ticket — so they are collapsed.
   */
  suppressed: AdgsQueueTicket[];
  /**
   * Rejected for a scope reason that is neither shape nor suppression: dedupe
   * against an in-flight CDID run, project gating, missing bot delegation, and
   * a wrong-wake flavor mismatch.
   */
  notInScope: AdgsQueueTicket[];
  recentSweeps: AdgsSweep[];
  pickupBudgetHours: number;
  generatedAt: string;
}

// ===========================================================================
// MARK: ADDIS status board (/admin/automation)
//
// The console rendering of `/a7y-addis-status`: one row per system, a stoplight
// per system, and the production chain APD is judged against. Composed
// server-side so the light and the figures behind it are decided in one place
// and unit-tested, rather than assembled by the browser from six endpoints.
// ===========================================================================

/**
 * The stoplight, with the command's own meanings:
 *   green — ran on schedule and did the right thing, INCLUDING correctly declining.
 *   amber — functioning, but a degraded cycle, an unreadable cause, or a standing
 *           backlog nothing will select.
 *   red   — malfunctioning, or a chain link through which no work can pass.
 */
export type AddisLight = 'green' | 'amber' | 'red';

export type AddisSystemKey = 'cdid' | 'adgs' | 'ahc' | 'apd';

/**
 * Every key the API's cron mirror carries — the four systems plus the CDID hot
 * lane, which is a separately pausable ingress rather than a session kind, so
 * `AddisSystemKey` structurally cannot hold it.
 */
export type AddisScheduleKey = AddisSystemKey | 'cdid-hot-lane';

/** One GitHub Actions run, reduced to what the board reports. */
export interface AddisWorkflowRun {
  /** Workflow file, e.g. `cdid.yml`. */
  workflow: string;
  runId: number;
  url: string;
  event: string;
  status: string;
  conclusion: string | null;
  startedAt: string;
  durationSeconds: number | null;
}

/**
 * Recent-run health for one workflow file.
 *
 * `runs: null` means the list could not be READ, which is not the same fact as
 * an empty list and must never render as "no runs" — the command's rule 3
 * (never guess a cause) is the reason these are separate states.
 */
export interface AddisWorkflowHealth {
  workflow: string;
  runs: AddisWorkflowRun[] | null;
  error: string | null;
  latest: AddisWorkflowRun | null;
  /** Completed runs in the window that concluded `success`, over the window size. */
  greenOfRecent: { green: number; of: number } | null;
  /**
   * The flight-recorder session the LATEST run produced, when it produced one.
   *
   * Lets a row link to `/admin/automation/:kind/:stamp` instead of to GitHub.
   * Null is the normal case rather than a failure: only the workflows that go
   * through `build-session.yml`, plus the ingress scheduler and the hot lane,
   * write to `auto_session_log` at all — everything else has no console detail
   * view to point at.
   */
  latestSession?: { kind: string; stamp: string } | null;
  /**
   * Which SECTION of its group this workflow belongs to — an APHID subsystem
   * (`PULSE`, `RAVEN`, `AXON`, `LATTICE`, `AQUIX`, `AHC`) in platform health, a
   * pipeline stage (`Ingress`, `CDID`, `Staging`, `Production`, …) in the two
   * pipelines.
   *
   * Was `subsystem`, which was true of the only group that had sections and
   * wrong the moment the pipelines got them (TPF, 2026-09-09). Every group's
   * membership is now declared the same way, in `WORKFLOW_GROUPS`, so a section
   * cannot drift from its group's workflow list: the list IS the sections,
   * flattened.
   *
   * Null only for a workflow whose group declares no sections, or one a client
   * receives from a server that predates them — a client renders those under an
   * `Unassigned` heading rather than dropping the row.
   */
  section?: string | null;
}

/** One labelled figure in a system's table. */
export interface AddisStatusRow {
  label: string;
  value: string;
  /**
   * An ISO instant this row leads with, rendered by the CLIENT in the viewer's
   * local timezone.
   *
   * The server must never format a time into `value`: it has no idea where the
   * reader is, so a baked timestamp is necessarily UTC, and a UTC time on a
   * console the operator reads at a glance is the exact failure the
   * local-timezone rule exists to prevent. `value` therefore carries only the
   * parts that are timezone-free (outcome, duration).
   */
  timestamp?: string | null;
  /** Draws attention without asserting a cause; `bad` never implies a diagnosis. */
  tone?: 'normal' | 'warn' | 'bad';
}

export interface AddisSystemStatus {
  key: AddisSystemKey;
  /** Display name, e.g. `CDID`. */
  label: string;
  light: AddisLight;
  /**
   * The command's rule 2: a system whose sole function IS a pipeline stage is
   * coloured by throughput, and the attribution goes in its header so nobody
   * reads the light as "this component is broken". APD is the only case
   * (`component healthy, blocked upstream at staging`); null everywhere else.
   */
  attribution: string | null;
  /** Why this light, in one sentence, derived from the figures below. */
  reason: string;
  scheduleLabel: string;
  nextScheduledAt: string | null;
  rows: AddisStatusRow[];
  workflows: AddisWorkflowHealth[];
  /**
   * Sources this section could not read, named rather than silently absent. A
   * section with entries here is at best amber: an unmeasurable system is not a
   * healthy one.
   */
  unreadable: string[];
}

/**
 * The production chain APD is judged against, from git refs and deploy runs.
 *
 * Every field is nullable because each is independently unreadable, and a null
 * renders as "unknown" rather than as a negative answer. The rejected-candidate
 * list is deliberately absent: it is a permanently-rejected set the coordinator
 * re-evaluates nightly, its ages grow forever, and reading it as "how long the
 * chain has been dry" was measured wrong on 2026-08-25.
 */
export interface AddisChain {
  /**
   * Name only, deliberately. Whether a candidate shipped is decided by pairing
   * its name against the release tag of the same name — the candidate tags are
   * annotated, so a ref's `object.sha` is the tag object rather than the commit,
   * and a SHA on this record would be a field nothing can correctly compare.
   */
  newestCandidateTag: { name: string } | null;
  /** A release tag at the same commit means that candidate already shipped. */
  matchingReleaseTag: string | null;
  candidateShipped: boolean | null;
  lastProdDeployAt: string | null;
  lastProdDeployConclusion: string | null;
  lastStagingDeployAt: string | null;
  /** Candidates are sealed by a staging deploy; no staging run since the last
   * candidate means no new candidate exists to ship. */
  stagingSinceCandidate: boolean | null;
  unreadable: string[];
}

/**
 * The three groups the overview is organised by (A7Y-3812).
 *
 * A DIFFERENT CUT of the same workflows than `AddisSystemKey`, deliberately,
 * and both survive. A system is a piece of automation with its own page, light
 * and queue; a group is a job to be done — keeping the platform healthy,
 * getting a release to production, getting development work landed — and one
 * system's workflows can sit in a group beside workflows that belong to no
 * ADDIS system at all (`canonical-verify.yml`, `state-invariants.yml`,
 * `bq-schema-sync.yml`). Collapsing the two would either lose the per-system
 * pages or lose those workflows.
 */
export type AddisGroupKey = 'platform-health' | 'release-pipeline' | 'dev-pipeline';

/**
 * One overview table: a job to be done, and every workflow file that does it.
 *
 * The light is derived from the group's own workflow runs by the SAME
 * `runHealth` rule a system's is, so the two cuts cannot disagree about what a
 * failing run means. It carries no queue depth — a group is not a queue.
 */
export interface AddisWorkflowGroup {
  key: AddisGroupKey;
  /** Display name, e.g. `Release Pipeline`. */
  label: string;
  /** One line saying what this group is responsible for. */
  description: string;
  light: AddisLight;
  /** Why this light, in one sentence, derived from the runs below. */
  reason: string;
  /**
   * One entry per workflow file, in declaration order — never sorted by health,
   * because the reader is looking up a file by name.
   *
   * Declaration order is SECTION-CONTIGUOUS: the server flattens the group's
   * sections into this list, so a client that buckets by `section` in first-
   * appearance order reproduces the declared section order without being told
   * it, and there is no second list of section names to drift.
   */
  workflows: AddisWorkflowHealth[];
  /** Run lists this group could not read, named rather than silently absent. */
  unreadable: string[];
}

export interface AddisStatus {
  /**
   * The overview's tables, in render order: platform health (which has its own
   * page and is filtered off the board), then dev, then release — the dev
   * pipeline is where work is produced and where a person intervenes, and
   * release is downstream of it.
   */
  groups: AddisWorkflowGroup[];
  systems: AddisSystemStatus[];
  chain: AddisChain;
  /** The four systems' lights. The per-system pages' summary; the overview
   *  renders no count of its own — TPF removed both `ADDIS status` straps on
   *  2026-09-09, and a field kept for a deleted strap is a field nobody reads. */
  counts: { green: number; amber: number; red: number };
  generatedAt: string;
}

// ===========================================================================
// MARK: CDID flow history (/admin/automation/cdid)
//
// Is CDID keeping up with its own queue? Answered as a flow balance over a
// trailing window: what ARRIVED in the eligible pool each day against what
// LEFT it, with the queue depth reconstructed alongside.
// ===========================================================================

/** One UTC day of queue flow. */
export interface CdidFlowDay {
  /** `YYYY-MM-DD`, UTC — the ledger partitions on UTC days. */
  date: string;
  /** Bot-delegated tickets created this day. The dominant arrival channel. */
  created: number;
  /**
   * Tickets that moved BACK into an eligible state from outside it — a
   * deferral, a reopen, a rejected review. A real arrival, and invisible if you
   * only count creations.
   */
  returned: number;
  /** `created + returned`. */
  arrivals: number;
  /**
   * Tickets that LEFT the eligible states. This is the drain the queue actually
   * feels, and it is deliberately not the same as `completed`: a ticket CDID
   * picks up stops occupying the queue the moment it moves to In Dev, whether
   * or not it ships later.
   */
  drained: number;
  /**
   * Tickets that reached `Done in Dev` — CDID's own definition of finished.
   * Reported beside `drained` because the two answer different questions and
   * conflating them is how a busy day looks productive.
   */
  completed: number;
  /**
   * Reconstructed eligible-queue depth at the END of this day.
   *
   * Anchored on the LIVE count and walked backwards
   * (`depth(d-1) = depth(d) - arrivals(d) + drained(d)`), so the newest point is
   * exact and error accumulates towards the oldest. See
   * `CdidFlowHistory.reconstructionCaveat`.
   */
  queueEnd: number;
}

export interface CdidFlowHistory {
  /** Oldest first. Clipped to `ledgerCoverageDays` when the ledger is younger. */
  days: CdidFlowDay[];
  /** Requested window length. */
  windowDays: number;
  /**
   * Days of `ticket_state_changed` history the ledger actually holds. The
   * ledger began 2026-08-15, so a 14-day window is only fully covered from
   * 2026-08-29 onward; before that the series is short rather than wrong, and
   * the console says which.
   */
  ledgerCoverageDays: number;
  /** The live eligible count the reconstruction is anchored on. */
  eligibleNow: number;
  totals: {
    arrivals: number;
    drained: number;
    completed: number;
    /** `arrivals - drained`. Negative means the queue shrank. */
    net: number;
  };
  /**
   * The headline answer. `keeping-up` when drain met or beat arrivals across
   * the window; `falling-behind` when it did not. `even` is reserved for an
   * exact tie, which is worth distinguishing from a narrow win.
   */
  verdict: 'keeping-up' | 'falling-behind' | 'even';
  /**
   * Why the oldest points are approximate, in the console's own words. Stated
   * as data rather than baked into the UI so the reason travels with the
   * numbers: eligibility also turns on project runnability and bot delegation,
   * neither of which emits a state transition, so a ticket that became eligible
   * because its project started is invisible to the walk-back.
   */
  reconstructionCaveat: string;
  /**
   * Ledger transitions dropped because their ticket is not delegated to the
   * bot — hand-moved work on the ADDIS / APHID / UKKO boards that is not CDID's
   * to drain. Measured 2026-09-02: 199 of 585 departures over 14 days. Zero
   * when the delegation sweep failed and the ledger was used unfiltered, which
   * `unreadable` then names.
   */
  excludedNonDelegated: number;
  generatedAt: string;
  /** Sources that could not be read, named rather than silently zero. */
  unreadable: string[];
}

/* ─────────────────────── A7Y-3475 Actions workload report ──────────────────
 *
 * The admin Workloads page (`/admin/automation/workloads`). Hybrid
 * verdict-live/evidence-precomputed, per the ticket's settled design (plan D1):
 * the daily producer (`scripts/actions-workload-producer.cjs`) writes ONE row
 * of `apptonomy-gcp.a7y_data.actions_workload_report` a day carrying a routing
 * DOCTRINE snapshot, per-workflow cost, and its own judgment (`findings`); the
 * Worker (`a7y-api/src/backends/admin/autobuild/workloads.ts`) joins LIVE
 * `A7Y_RUNNER_*` variable values against that row's doctrine so a routing
 * change — an exile, a restore — is visible within the endpoint's 60s cache
 * rather than only on the next producer run. The class LIST itself (which
 * variables exist, which classes are KEEP-covered) travels only inside the
 * row (plan D3) — this file never hardcodes one.
 */

/**
 * Where one routing class's LIVE value sits relative to doctrine.
 *
 * - `on-pool`             — the bare `ukko` label or an `ukko-`-prefixed one.
 * - `off-pool-overflow`   — the one class overflow is allowed to move, moved,
 *                           with the controller's own flag corroborating it.
 * - `off-pool-failover`   — the controller recorded a general health failover.
 * - `off-pool-disabled`   — an operator deliberately took this class off-pool.
 * - `off-pool-exile`      — off-pool for no recorded reason — the stuck signal.
 * - `absent-fail-safe`    — the variable doesn't exist AND doctrine says this
 *                           class's cutover hasn't landed — never a finding.
 * - `absent-unexpected`   — the variable doesn't exist but doctrine expects a
 *                           cut-over class to be set — amber, not a fault.
 * - `unreadable`          — the live variables fetch itself failed, so this
 *                           class's actual state is simply unknown.
 */
export type WorkloadRoutingStatus =
  | 'on-pool'
  | 'off-pool-overflow'
  | 'off-pool-failover'
  | 'off-pool-disabled'
  | 'off-pool-exile'
  | 'absent-fail-safe'
  | 'absent-unexpected'
  | 'unreadable';

/** One routing class's row in the console's routing table. */
export interface WorkloadRoutingRow {
  /** The `A7Y_RUNNER_*` repository variable name. */
  class: string;
  /** Doctrine's UKKO value for this class. */
  ukkoValue: string;
  /** Doctrine's GitHub-hosted fallback for this class. */
  githubValue: string;
  /** Whether this class's cutover has landed, per the row's doctrine snapshot. */
  cutover: boolean;
  /** The live repository variable's current value; null when absent. */
  liveValue: string | null;
  status: WorkloadRoutingStatus;
  /**
   * A `gh variable set` command restoring this class to `ukkoValue`, present
   * only for `off-pool-exile`. TEXT ONLY, never an action button — this page
   * is read-only by design (plan exclusion #2: no remediation from the UI).
   */
  restoreCommand: string | null;
}

/** The failover controller's persisted intent, as read from `A7Y_RUNNER_FAILOVER_STATE`. */
export interface WorkloadFailoverState {
  overflowActive: boolean;
  failedOverAt: string | null;
  disabledClasses: string[];
}

export type WorkloadFindingKind = 'EXILE' | 'DOCTRINE_VIOLATION' | 'SPLIT_PLACEMENT' | 'VERIFY';

/**
 * One entry in the findings table, most-costly-first. `EXILE` findings are
 * synthesized LIVE by the Worker from the shared predicate in
 * `scripts/lib/actions-workload-findings.cjs` (adversarial finding A5) rather
 * than passed through from the row, so a routing change is visible without
 * waiting for the next producer run; `DOCTRINE_VIOLATION`/`SPLIT_PLACEMENT`/
 * `VERIFY` come from the row's own `findings` column as the daily producer
 * computed them.
 */
export interface WorkloadFinding {
  kind: WorkloadFindingKind;
  /** The affected workflow file; null for a class-level finding (EXILE, VERIFY). */
  workflow: string | null;
  /** The affected routing class; present for EXILE and VERIFY. */
  class?: string | null;
  expected: string | null;
  actual: string | null;
  /**
   * Day-granular, rendered "≥ <date>". Null with no qualifying prior-row
   * history, and ALWAYS null on a live-synthesized EXILE finding — the Worker
   * has no prior-row walk available to it, and that is what "≥" is for (plan
   * A5: "sinceAtLeast stays null on day one").
   */
  sinceAtLeast: string | null;
  /** Null renders as "—": DOCTRINE_VIOLATION and SPLIT_PLACEMENT carry no cost figure by design. */
  estMonthlyUsd: number | null;
  /** VERIFY only. */
  overflowActive?: boolean;
  poolEvidence?: WorkloadPoolEvidence;
  classification?: 'ok' | 'overflow-active' | 'stuck-exile';
}

/** Whether the UKKO pool showed signs of life in a bounded window (plan A4). */
export interface WorkloadPoolEvidence {
  workflowsOnUkko: string[];
  ukkoJobCount: number;
  windowHours: number;
}

/** One cost/placement cell — the engine's exact-run-count-scaled sample. */
export interface WorkloadWorkflowCells {
  runs: number;
  ghRuns: number;
  ukkoRuns: number;
  ghOk: number | null;
  ukkoOk: number | null;
  runOk: number | null;
  ghCost: number;
  sampled: number;
}

/** Per-workflow summary + weekly cost/placement, as the engine emits it. */
export interface WorkloadWorkflowSummary {
  name: string;
  path: string | null;
  summary: WorkloadWorkflowCells;
  /** ISO-week (Monday) → cells, or null for a week the workflow had no runs in. */
  weekly: Record<string, WorkloadWorkflowCells | null>;
}

/** The `/admin/autobuild/workloads` payload. */
export interface WorkloadsStatus {
  verdict: 'red' | 'amber' | 'green';
  routing: WorkloadRoutingRow[];
  /** Null when neither the live fetch nor the row's audit-time snapshot was readable. */
  failoverState: WorkloadFailoverState | null;
  findings: WorkloadFinding[];
  workflows: WorkloadWorkflowSummary[];
  weeks: string[];
  /** The engine's own summary window, in days; null with no report row. */
  summaryDays: number | null;
  poolEvidence: WorkloadPoolEvidence | null;
  /** The report row's own `run_at` — when the daily producer last wrote a row. Null with no row yet. */
  producedAt: string | null;
  /** This endpoint's own composition time. */
  generatedAt: string;
  staleAfterHours: 30;
  /** True when `producedAt` is null or older than `staleAfterHours`. */
  stale: boolean;
  /** Sources that could not be read, named rather than silently zero. */
  unreadable: string[];
}

// ---------------------------------------------------------------------------
// MARK: UKKO slots — /admin/autobuild/slots (A7Y-3600)
// ---------------------------------------------------------------------------

export const UKKO_SLOT_CLASSES = ['session', 'verify', 'deploy'] as const;
export type UkkoSlotClass = (typeof UKKO_SLOT_CLASSES)[number];

export const UKKO_SLOT_STATES = ['idle', 'starting', 'running', 'stuck', 'overtime', 'unknown'] as const;
export type SlotState = (typeof UKKO_SLOT_STATES)[number];

export interface UkkoSlotWork {
  runId: string;
  runAttempt: number | null;
  runUrl: string;
  workflowName: string; // run.name — "Canonical Verify", "Deploy Staging", "ADGS"
  workflowFile: string; // basename(run.path) — "cdid.yml"
  displayTitle: string; // run.display_title, verbatim
  jobId: string;
  jobName: string;
  requestedLabel: string | null;
  /** D7/D15: the matched execution step, or null when this job is not silence-scoped. */
  executionStepName: string | null;
  executionStartedAt: string | null;
  startedAt: string; // job.started_at
  elapsedMs: number; // from job.started_at

  sessionKind: string | null;
  sessionFamily: string | null;
  sessionStamp: string | null;
  /** D6/PA6: 'legacy-unclassified' when the run recorded conflicting markers. */
  component: string | null;
  timeoutMinutes: number | null;
  ledgerStartedAt: string | null;
  /** D6/PA5: which clock the overtime verdict used. */
  overtimeClockOrigin: 'ledger' | 'none';

  tickets: string[];
  driverAccount: string | null;

  lastActivityAt: string | null;
  lastHeartbeatAt: string | null;
  silentForMs: number | null;
  /** D15: 'ledger' | 'execution-step-start' | 'none'; null when not silence-scoped. */
  silenceClockOrigin: string | null;
  silenceThresholdMs: number | null;
}

export interface UkkoSlotStatus {
  index: number;
  slotClass: UkkoSlotClass;
  labels: string[];
  state: SlotState;
  /** Always present, including for `idle`. */
  reason: string;
  work: UkkoSlotWork | null;
}

export interface UkkoSlotQueueEntry {
  runId: string;
  runUrl: string;
  jobId: string; // PA3
  jobName: string; // PA3
  workflowName: string;
  displayTitle: string;
  /**
   * A7Y-5411: the queued job's own created_at — when it began waiting for a
   * runner. A run's created_at would date a follow-on job's wait (e.g.
   * `release-lease`) from the session's start; it is used only when the job
   * carries no created_at.
   */
  ageFrom: 'job-created' | 'run-created';
  queuedAt: string;
  ageMs: number;
}

export interface UkkoSlotQueue {
  label: string; // 'ukko' | 'ukko-session' | 'ukko-verify' | 'ukko-deploy'
  starved: UkkoSlotQueueEntry[];
  queuedCount: number;
}

export interface UkkoSlotFinding {
  code: 'unlisted-slot' | 'slot-double-claim';
  summary: string;
  slotIndex: number | null;
  runIds: string[];
}

export interface UkkoSlotsStatus {
  slots: UkkoSlotStatus[];
  queues: UkkoSlotQueue[];
  findings: UkkoSlotFinding[];
  silenceThresholdMs: number;
  staleQueuedRunMs: number;
  liveRunsEnumerated: number;
  queuedRunsEnumerated: number;
  jobFetchesAttempted: number;
  /** D11 case (c): true when a cap bit or a jobs fetch failed. */
  truncated: boolean;
  generatedAt: string;
  unreadable: string[];
}

// ---------------------------------------------------------------------------
// MARK: ADDIS enablement (`/admin/automation/overview`)
// ---------------------------------------------------------------------------

/**
 * A system's pause verdict, in the vocabulary `scripts/lib/automation-pause.cjs`
 * prints. The console and the CLI must not have two ideas of what "on" means.
 *
 * `unknown` is a real third state and never renders as either of the others:
 * the flag could not be read, or it holds a value the strict parser rejects
 * (`TRUE`, `yes`, `1`). The runbook's words for it are "not a clean bill of
 * health" — a surface that is unverified, not one that is running.
 */
export type AddisPauseVerdict = 'running' | 'paused' | 'unknown';

/**
 * A workflow on a system's surface that someone disabled by hand.
 *
 * A fault is reported SEPARATELY from the verdict and never changes it — a
 * pause is a flag, and no verb in the pause CLI can enable or disable a
 * workflow, so `resume` does not clear one. Conflating the two is the exact
 * failure the flag shape was introduced to retire.
 */
export interface AddisEnablementFault {
  workflow: string;
  /** GitHub's own word: `disabled_manually`, `disabled_inactivity`, `unknown`. */
  state: string;
  /** `gh workflow enable cdid.yml` */
  remedy: string;
}

export interface AddisEnablementSurfaceRow {
  workflow: string;
  role: string;
  state: string;
  disabled: boolean;
}

export interface AddisEnablementFlag {
  variable: string;
  /** Verbatim, so a rejected value is visible rather than normalized away. */
  value: string;
  /** `env` when the variable is set, `default` when absent and the code's default applies. */
  source: 'env' | 'default';
}

export interface AddisEnablementSystem {
  /** The registry key — `cdid`, `cdid-hot-lane`, `adgs`, `apd` — or `ahc`. */
  key: string;
  /** `CDID standard ingress` — the registry's own name for it. */
  label: string;
  verdict: AddisPauseVerdict;
  /**
   * Which variable answered. The fleet switch outranks a system's own flag,
   * mirroring `guardIngressDispatch()`'s branch order, so a reader is never
   * told a subsystem is paused while the whole fleet is down.
   */
  decidedBy: string | null;
  /** Null for AHC, which has no flag and is paused by disabling its workflow. */
  flag: AddisEnablementFlag | null;
  /** What stops while this system is paused. */
  stops: string;
  /** What keeps running — the most useful line to read beside a paused system. */
  stillAdmitted: string;
  /** `/a7y-adgs-resume`, or null where there is no command. */
  resumeCommand: string | null;
  /** `node scripts/adgs-pause.cjs resume`, or the `gh` line for AHC. */
  resumeScript: string;
  /** How long until a refusal is next visible in the Actions tab. */
  nextRefusal: string;
  /** The literal cron the cadence comes from, UTC as GitHub defines it. */
  cron: string | null;
  surface: AddisEnablementSurfaceRow[];
  faults: AddisEnablementFault[];
  /** Where this system's own console page lives, when it has one. */
  detailPath: string | null;
  /** False where no flag exists to write — the console must not offer a switch. */
  togglable: boolean;
}

export interface AddisEnablement {
  schema: 'a7y.addis-enablement/v2';
  /**
   * The fleet kill switch, reported on its own because it is not a system: it
   * refuses every ingress of every purpose, stops the automated production
   * deploy as a side effect, and no per-system command reads or writes it.
   */
  fleet: { variable: string; value: string; paused: boolean };
  systems: AddisEnablementSystem[];
  generatedAt: string;
}

/** Request body of `POST /admin/autobuild/pause/:system`. */
export interface AddisPauseRequest {
  paused: boolean;
}

export interface AddisPauseResponse {
  system: string;
  /** Re-read from GitHub after the write — never the value that was sent. */
  verdict: AddisPauseVerdict;
  variable: string;
  value: string;
  /** The admin who made the change; the variable's own metadata cannot say. */
  by: string;
}

// ── CDID cycles (TPF, 2026-09-16) ─────────────────────────────────────────────
//
// The Activity tab's unit was the GitHub Actions run — one row per session
// stamp. Every question the page exists to answer is about a CYCLE and its
// tickets: is CDID healthy, what became of the recent material runs, what is
// it spending, how many tickets did each run resolve. One cycle is an ingress
// firing (or a scheduled orchestrator, or a hot-lane admission), one or more
// worker runs, and one to four lander waves — measured over ten days to
// 2026-09-16, 86 cycles landed through one wave, 15 through two, 11 through
// three, one through four; and one wave carried nine tickets for six cycles.
// Rendered as sessions, that is separate rows, hidden rows, and a bare zero on
// the row that started the work.
//
// The 26 of 30 ingress firings that armed nothing are a heartbeat, not runs,
// and appear here only as a count. The lander's own rows stay hidden and are
// reachable from the cycle whose tickets they landed.

/** How a cycle came to exist. `unknown` is a recorded gap, never a guess. */
export type CdidCycleTrigger = 'schedule' | 'ingress' | 'hot-lane' | 'manual' | 'unknown';

/**
 * Where a cycle is in its life. `stuck` is derived from the lander's own wave
 * reports, not from age: a bundle the lander has reported blocked across
 * successive waves is stuck, whatever the clock says.
 */
export type CdidCycleState = 'in-flight' | 'settled' | 'stuck';

/**
 * Model tokens a worker consumed, from the SCRIBE ledger's task-economics
 * payload (`economics.roles[].tokens`). This is what is RECORDED. USD is not:
 * every worker row in the window carries `cost_usd: null` with
 * `cost_source: unavailable_no_cost_at_task_boundary`, so a dollar figure here
 * would be an estimate dressed as a measurement. Minutes and tokens are the
 * honest budget units, and the console says so rather than pricing them.
 */
export interface CdidTokenUsage {
  input: number;
  output: number;
  cacheRead: number;
  cacheCreation: number;
  turns: number;
}

/** One worker attempt inside a cycle. */
export interface CdidCycleWorker {
  stamp: string;
  runId: string | null;
  bundleId: string | null;
  attempt: number | null;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number | null;
  outcome: AutobuildOutcome;
  runnerHost: AutobuildRunnerHost | null;
  /**
   * The worker's own verdict on its bundle, from its `worker <verdict>:` log
   * line — `green`, `blocked`, `time-gated`, `requeued`, `failed` — or null
   * when it wrote none. This is the word that makes a zero legible: "dispatched
   * one, blocked" reads very differently from "dispatched one, still running".
   */
  verdict: string | null;
  /** The reason text after the verdict, verbatim from the worker, or null. */
  reason: string | null;
  tokens: CdidTokenUsage | null;
}

/** One ticket a cycle touched, with its current fate. */
export interface CdidCycleTicket {
  id: string;
  title: string | null;
  /** From the attributed facts, via the same derivation the Stage column uses. */
  stage: CdidLifecycleState | null;
  stageEnteredAt: string | null;
  /** Highest worker attempt seen for this ticket; 0 when no worker ran it. */
  attempts: number;
  /** The worker's last stated reason for not landing it, or null. */
  reason: string | null;
  /**
   * The lander run that landed it, as that run's stamp (its GitHub run id), or
   * null while unlanded. Recovered from the worker branch the lander names on
   * its `landed` row — the join the console never had.
   */
  landedBy: string | null;
  landedAt: string | null;
}

export interface CdidCycle {
  stamp: string;
  runId: string | null;
  trigger: CdidCycleTrigger;
  ingress: AutobuildIngress | null;
  startedAt: string;
  /**
   * When the cycle's last ticket reached a terminal stage, or null while any
   * ticket is still open. A cycle settles hours after its own session ends —
   * the session finishes in seconds, the landing is written by another run.
   */
  settledAt: string | null;
  state: CdidCycleState;
  runnerHost: AutobuildRunnerHost | null;
  runnerName: string | null;
  tickets: CdidCycleTicket[];
  resolvedCount: number;
  blockedCount: number;
  parkedCount: number;
  deferredCount: number;
  workers: CdidCycleWorker[];
  /** Total worker attempts. Above `tickets.length` is the rework signal. */
  attempts: number;
  /** Sum of worker durations — the budget unit that is always recorded. */
  workerSeconds: number;
  /** Sum over workers that recorded tokens; null when none did. */
  tokens: CdidTokenUsage | null;
  /** Dispatch to last landing; elapsed since dispatch while in flight. */
  durationSeconds: number;
  /** Distinct lander runs that landed this cycle's tickets. */
  landerRunIds: string[];
}

/**
 * A bundle the lander reports as blocked. Derived from consecutive
 * `cdid.wave-report/v1` rows, so `wavesBlocked` is how many waves in a row
 * have carried it and `blockedSince` is the first of them in the window.
 */
export interface CdidStuckBundle {
  bundleId: string;
  /** Parsed from the bundle id (`ingress-a7y-3929` → `A7Y-3929`), or null. */
  ticketId: string | null;
  blockedSince: string;
  wavesBlocked: number;
}

/** One day of spend and output, for the seven-day baseline. */
export interface CdidSpendDay {
  /** UTC calendar day, `YYYY-MM-DD`. The client renders in its own zone. */
  day: string;
  workerSeconds: number;
  attempts: number;
  landed: number;
}

export type CdidHealthVerdict = 'healthy' | 'degraded' | 'stuck';

export interface CdidHealth {
  verdict: CdidHealthVerdict;
  /** One line per rule that fired, in the words the strip shows. Empty when healthy. */
  reasons: string[];
  lastLandedAt: string | null;
  landed24h: number;
  landed7d: number;
  inFlightCycles: number;
  inFlightWorkers: number;
  stuck: CdidStuckBundle[];
  /** The newest lander wave's start, or null when none ran in the window. */
  landerLastRanAt: string | null;
  /** The heartbeat: firings in the last 24h, and how many armed a bundle. */
  ingress24h: { fired: number; armed: number };
  hotLane24h: { fired: number; armed: number };
  spend: {
    workerSecondsToday: number;
    /** Mean over the seven completed UTC days before today. */
    workerSeconds7dAvgPerDay: number;
    /** Attempts per ticket over seven days; null with no tickets. */
    attemptsPerTicket7d: number | null;
    byDay: CdidSpendDay[];
  };
}

/** Response of `GET /admin/autobuild/cdid/cycles`. */
export interface CdidCyclesResponse {
  health: CdidHealth;
  /** Material cycles only — every one dispatched at least one worker. Newest first. */
  cycles: CdidCycle[];
  generatedAt: string;
}
